// The single extension point for adding new FTMS machine types.
// To support a new piece of equipment in Phase 2: implement the parser,
// add a metric interface in types.ts, and register an entry below.

import {
  CROSS_TRAINER_DATA_UUID,
  INDOOR_BIKE_DATA_UUID,
  ROWER_DATA_UUID,
  STAIR_CLIMBER_DATA_UUID,
  STEP_CLIMBER_DATA_UUID,
  TREADMILL_DATA_UUID,
} from "./constants";
import { parseCrossTrainerData } from "./parsers/crossTrainer";
import { parseIndoorBikeData } from "./parsers/indoorBike";
import { parseRowerData } from "./parsers/rower";
import { parseStairClimberData } from "./parsers/stairClimber";
import { parseStepClimberData } from "./parsers/stepClimber";
import { parseTreadmillData } from "./parsers/treadmill";
import type { EquipmentType, FtmsMetrics } from "./types";

export interface EquipmentDescriptor {
  type: EquipmentType;
  dataCharUuid: number;
  label: string;
  /** lucide-react icon name — looked up dynamically by UI consumers. */
  icon: string;
  /** Parser implementations are *defensive*: they never throw. */
  parse: (view: DataView) => FtmsMetrics;
  /** Component name; UI maps this to a dashboard component. */
  dashboard: string;
  /** Phase 1 = fully wired, Phase 2 = stubbed parser, not yet user-facing. */
  status: "phase1" | "phase2";
}

export const EQUIPMENT: Record<EquipmentType, EquipmentDescriptor> = {
  indoor_bike: {
    type: "indoor_bike",
    dataCharUuid: INDOOR_BIKE_DATA_UUID,
    label: "Indoor Bike",
    icon: "Bike",
    parse: parseIndoorBikeData,
    dashboard: "BikeDashboard",
    status: "phase1",
  },
  rower: {
    type: "rower",
    dataCharUuid: ROWER_DATA_UUID,
    label: "Rower",
    icon: "Sailboat",
    parse: parseRowerData,
    dashboard: "RowerDashboard",
    status: "phase1",
  },
  treadmill: {
    type: "treadmill",
    dataCharUuid: TREADMILL_DATA_UUID,
    label: "Treadmill",
    icon: "Footprints",
    parse: parseTreadmillData,
    dashboard: "TreadmillDashboard",
    status: "phase2",
  },
  cross_trainer: {
    type: "cross_trainer",
    dataCharUuid: CROSS_TRAINER_DATA_UUID,
    label: "Cross Trainer",
    icon: "PersonStanding",
    parse: parseCrossTrainerData,
    dashboard: "CrossTrainerDashboard",
    status: "phase2",
  },
  step_climber: {
    type: "step_climber",
    dataCharUuid: STEP_CLIMBER_DATA_UUID,
    label: "Step Climber",
    icon: "StepForward",
    parse: parseStepClimberData,
    dashboard: "StepClimberDashboard",
    status: "phase2",
  },
  stair_climber: {
    type: "stair_climber",
    dataCharUuid: STAIR_CLIMBER_DATA_UUID,
    label: "Stair Climber",
    icon: "StretchHorizontal",
    parse: parseStairClimberData,
    dashboard: "StairClimberDashboard",
    status: "phase2",
  },
};

/** Iterable view of all entries, useful for classification + UI rendering. */
export const EQUIPMENT_ENTRIES: ReadonlyArray<EquipmentDescriptor> =
  Object.values(EQUIPMENT);
